/**
 * Error Screen Component
 * Displays various error states for invalid sessions or system errors
 */

import {
  VerificationScreenTemplate,
  ContentCard,
  VerticalStack,
} from '../shared';
import { designTokens } from '../../constants/designTokens';
import { useBrand } from '../../contexts/BrandContext';
import { useLanguage } from '../../i18n';

export interface ErrorScreenProps {
  errorType:
    | 'invalid-session'
    | 'expired-session'
    | 'network-error'
    | 'unsupported-browser'
    | 'camera-denied'
    | 'missing-return-url'
    | 'direct-access'
    | 'verification-unavailable'
    | 'subscription-required'
    | 'plan-limit-reached'
    | 'sandbox-limit-reached';
  title?: string;
  description?: string;
  onRetry?: () => void;
  onCancel?: () => void;
  portalUrl?: string;
}

export function ErrorScreen({
  errorType,
  title,
  description,
  onRetry,
  onCancel,
  portalUrl,
}: ErrorScreenProps) {
  const { t } = useLanguage();
  const brand = useBrand();
  const productName = brand?.productName || t('common.productFallback');
  const supportUrl = brand?.supportUrl;

  // Error configurations
  const errorConfigs = {
    'invalid-session': {
      title: t('errorScreen.invalidSession.title'),
      description: t('errorScreen.invalidSession.description'),
      icon: '⚠️',
      showRetry: false,
      primaryAction: t('errorScreen.invalidSession.primaryAction'),
      suggestions: [
        t('errorScreen.invalidSession.suggestion1'),
        t('errorScreen.invalidSession.suggestion2'),
        t('errorScreen.invalidSession.suggestion3'),
      ],
    },
    'expired-session': {
      title: t('errorScreen.expiredSession.title'),
      description: t('errorScreen.expiredSession.description'),
      icon: '⏰',
      showRetry: false,
      primaryAction: t('errorScreen.expiredSession.primaryAction'),
      suggestions: [
        t('errorScreen.expiredSession.suggestion1'),
        t('errorScreen.expiredSession.suggestion2'),
        t('errorScreen.expiredSession.suggestion3'),
      ],
    },
    'network-error': {
      title: t('errorScreen.network.title'),
      description: t('errorScreen.network.description'),
      icon: '🌐',
      showRetry: true,
      primaryAction: t('common.tryAgain'),
      suggestions: [
        t('errorScreen.network.suggestion1'),
        t('errorScreen.network.suggestion2'),
        t('errorScreen.network.suggestion3'),
        t('errorScreen.network.suggestion4'),
      ],
    },
    'unsupported-browser': {
      title: t('errorScreen.unsupported.title'),
      description: t('errorScreen.unsupported.description'),
      icon: '🌍',
      showRetry: false,
      primaryAction: t('errorScreen.unsupported.primaryAction'),
      suggestions: [
        t('errorScreen.unsupported.suggestion1'),
        t('errorScreen.unsupported.suggestion2'),
        t('errorScreen.unsupported.suggestion3'),
      ],
    },
    'camera-denied': {
      title: t('errorScreen.cameraDenied.title'),
      description: t('errorScreen.cameraDenied.description'),
      icon: '📷',
      showRetry: true,
      primaryAction: t('common.enableCamera'),
      suggestions: [
        t('errorScreen.cameraDenied.suggestion1'),
        t('errorScreen.cameraDenied.suggestion2'),
        t('errorScreen.cameraDenied.suggestion3'),
      ],
    },
    'missing-return-url': {
      title: t('errorScreen.missingReturnUrl.title'),
      description: t('errorScreen.missingReturnUrl.description'),
      icon: '🔧',
      showRetry: false,
      primaryAction: t('errorScreen.missingReturnUrl.primaryAction'),
      suggestions: [
        t('errorScreen.missingReturnUrl.suggestion1'),
        t('errorScreen.missingReturnUrl.suggestion2'),
        t('errorScreen.missingReturnUrl.suggestion3', { productName }),
        t('errorScreen.missingReturnUrl.suggestion4'),
      ],
    },
    'direct-access': {
      title: t('errorScreen.directAccess.title'),
      description: t('errorScreen.directAccess.description', { productName }),
      icon: '',
      svgIcon: '/icons/blocked.svg',
      showSuggestions: false,
      showRetry: false,
      primaryAction: t('errorScreen.directAccess.primaryAction'),
      suggestions: [],
    },
    'verification-unavailable': {
      title: t('lifecycleRecovery.loadTitle'),
      description: t('lifecycleRecovery.loadDescription'),
      icon: '',
      svgIcon: '/icons/unsuccessful.svg',
      showSuggestions: false,
      showRetry: true,
      primaryAction: t('common.tryAgain'),
      suggestions: [],
    },
    'subscription-required': {
      title: t('errorScreen.subscriptionRequired.title'),
      description: t('errorScreen.subscriptionRequired.description'),
      icon: '',
      svgIcon: '/icons/blocked.svg',
      showSuggestions: false,
      showRetry: false,
      primaryAction: t('errorScreen.subscriptionRequired.primaryAction'),
      portalAction: t('errorScreen.subscriptionRequired.portalAction'),
      suggestions: [],
    },
    'plan-limit-reached': {
      title: t('errorScreen.planLimitReached.title'),
      description: t('errorScreen.planLimitReached.description'),
      icon: '',
      svgIcon: '/icons/blocked.svg',
      showSuggestions: false,
      showRetry: false,
      primaryAction: t('errorScreen.planLimitReached.primaryAction'),
      portalAction: t('errorScreen.planLimitReached.portalAction'),
      suggestions: [],
    },
    'sandbox-limit-reached': {
      title: t('errorScreen.sandboxLimitReached.title'),
      description: t('errorScreen.sandboxLimitReached.description'),
      icon: '',
      svgIcon: '/icons/blocked.svg',
      showSuggestions: false,
      showRetry: false,
      primaryAction: t('errorScreen.sandboxLimitReached.primaryAction'),
      portalAction: t('errorScreen.sandboxLimitReached.portalAction'),
      suggestions: [],
    },
  };

  const config = errorConfigs[errorType];
  const portalActionLabel =
    portalUrl && 'portalAction' in config ? config.portalAction : undefined;
  const primaryActionLabel = portalActionLabel || config.primaryAction;

  const handlePrimaryAction = () => {
    if (config.showRetry && onRetry) {
      onRetry();
    } else if (errorType === 'unsupported-browser') {
      window.open('https://www.google.com/chrome/', '_blank');
    } else if (errorType === 'invalid-session') {
      if (supportUrl) {
        window.open(supportUrl, '_blank');
      } else if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    } else if (errorType === 'missing-return-url') {
      // Go back to merchant site if possible
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    } else if (errorType === 'direct-access') {
      // Close the window/tab since this is invalid direct access
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    } else if (errorType === 'expired-session') {
      if (onCancel) {
        onCancel();
        return;
      }
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    } else if (
      errorType === 'subscription-required' ||
      errorType === 'plan-limit-reached' ||
      errorType === 'sandbox-limit-reached'
    ) {
      if (portalUrl) {
        window.open(portalUrl, '_blank');
        return;
      }
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    } else if (onRetry) {
      onRetry();
    }
  };


  // Check if this error type uses SVG icon (clean design) or emoji (legacy)
  const usesSvgIcon = 'svgIcon' in config && config.svgIcon;
  const showSuggestions = !('showSuggestions' in config && config.showSuggestions === false);

  return (
    <VerificationScreenTemplate
      title={title || config.title}
      description={description || config.description}
      showProgress={false}
      showBackButton={false}
      desktopOptimized={true}
      icon={usesSvgIcon ? {
        src: config.svgIcon as string,
        alt: t('errorScreen.directAccess.iconAlt'),
        size: '3xl',
      } : undefined}
      primaryAction={{
        label: primaryActionLabel,
        onClick: handlePrimaryAction,
      }}
    >
      {/* Only show children content for error types that don't use SVG icon */}
      {!usesSvgIcon && (
        <VerticalStack spacing="lg">
          {/* Error Icon (emoji) */}
          <div className="error-display">
            <div className="error-icon">{config.icon}</div>
          </div>

          {/* Error Details */}
          {showSuggestions && config.suggestions.length > 0 && (
            <ContentCard padding="md" className="error-details">
              <div className="error-content">
                <h3 className="error-subtitle">{t('errorScreen.helpTitle')}</h3>
                <ul className="error-suggestions">
                  {config.suggestions.map((suggestion) => (
                    <li key={suggestion}>{suggestion}</li>
                  ))}
                </ul>
              </div>
            </ContentCard>
          )}
        </VerticalStack>
      )}

      <style>{`
        .error-display {
          text-align: center;
          margin: ${designTokens.spacing.lg} 0;
        }

        .error-icon {
          font-size: 64px;
          margin-bottom: ${designTokens.spacing.md};
          opacity: 0.9;
          filter: grayscale(100%) brightness(0.8);
        }

        .error-details {
          background-color: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(10px);
        }

        .error-content {
          text-align: left;
          width: 100%;
        }

        .error-subtitle {
          font-size: ${designTokens.typography.fontSize.sm};
          font-weight: 600;
          margin-bottom: ${designTokens.spacing.md};
          color: ${designTokens.colors.text.primary};
          margin-top: 0;
          opacity: 0.9;
        }

        .error-suggestions {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .error-suggestions li {
          font-size: ${designTokens.typography.fontSize.sm};
          color: ${designTokens.colors.text.secondary};
          margin-bottom: ${designTokens.spacing.xs};
          position: relative;
          padding-left: ${designTokens.spacing.md};
          line-height: 1.5;
        }

        .error-suggestions li:before {
          content: '•';
          color: ${designTokens.colors.blue.text};
          position: absolute;
          left: 0;
          opacity: 0.6;
        }

        /* Mobile adjustments */
        ${designTokens.media.mobile} {
          .error-icon {
            font-size: 48px;
          }
        }
      `}</style>
    </VerificationScreenTemplate>
  );
}

export default ErrorScreen;
